// src/constants/uiRegistry.js

/**
 * UI Registry (single source of truth)
 * - Centralizes labels, nav, subnav, fields, filters, and deep vertical pages per industry
 * - Role-based visibility (items may include roles: ['Admin','Manager'])
 * - Per-tenant overrides via localStorage('ui_overrides'):
 *   {
 *     "all": { ...merge onto all industries... },
 *     "industries": {
 *       "pest_control": { ...merge only this industry... }
 *     }
 *   }
 *
 * PERFORMANCE NOTES:
 * - This file is just metadata; it does not import components.
 * - Keep routes lazy-loaded at the router level so adding pages here doesn’t bloat bundles.
 * - Navigation/TopBar read only the current industry’s slice.
 */

/* ───────────────── Base (defaults for any industry) ───────────────── */

const base = {
    labels: {
      customers: 'Customers',
      leads: 'Leads',
      deals: 'Deals',
      buyers: 'Customers',
      sellers: 'Leads',
    },
  
    // Keep icons limited to what Navigation ICON_MAP already supports
    // (FiHome, FiFileText, FiUsers, FiCalendar, FiPhone, FiTrendingUp, FiBarChart2, FiTag, FiSettings)
    nav: [
      { path: '/dashboard',      label: 'Dashboard',      icon: 'FiHome' },
      { path: '/leads',          label: 'Leads',          icon: 'FiFileText' },
      { path: '/customers',      label: 'Customers',      icon: 'FiUsers' },
      { path: '/schedule',       label: 'Schedule',       icon: 'FiCalendar' },
      { path: '/communication',  label: 'Communications', icon: 'FiMessageSquare' },
      { path: '/finance',        label: 'Finance',        icon: 'FiDollarSign' },
      { path: '/analytics',      label: 'Analytics',      icon: 'FiTrendingUp' },
      { path: '/revival',       label: 'Quotes',         icon: 'FiClipboard' },
      { path: '/operations',    label: 'Operations',        icon: 'FiTruck' },
      { path: '/team',           label: 'Team',           icon: 'FiUserPlus' },
      { path: '/settings',       label: 'Settings',       icon: 'FiSettings' },
    ],
  
    // Subnav per primary route. Use consistent snake_case keys in code.
    subnav: {
      '/dashboard': [
        { key: 'overview',   label: 'Overview',    path: '/dashboard' },
        { key: 'my_tasks',   label: 'My Tasks',    path: '/dashboard/tasks' },
        { key: 'alerts',     label: 'Alerts',      path: '/dashboard/alerts' },
      ],

      // AI section — Proposals (pending, actionable) + Activity (ledger history).
      '/ai': [
        { key: 'proposals', label: 'Proposals', path: '/ai/proposals' },
        { key: 'activity',  label: 'Activity',  path: '/ai/activity' },
      ],
  
      '/leads': [
        { key: 'new',       label: 'New Leads',  path: '/leads/new' },
        { key: 'queue',     label: 'Work Queue', path: '/leads/queue' },
        { key: 'pipeline',  label: 'Pipeline',   path: '/leads/pipeline' },
      ],
  
      '/customers': [
        { key: 'list',     label: 'Customers',      path: '/customers/list' },
        { key: 'import',   label: 'Import',         path: '/customers/import', roles: ['Admin','Manager'] },
        { key: 'care',     label: 'Customer Care',  path: '/customers/care' },
        { key: 'insights', label: 'Customer Insights', path: '/customers/ai' },
        { key: 'revival',  label: 'Quotes',  path: '/customers/revival' },
      ],
  
      // One scheduling nav for every tenant: Month · Dispatch · Jobs. (Was
      // Dispatch-only here, with a pest override — two different nav patterns in
      // the same section. Unified so t3 and t6 read identically.)
      '/schedule': [
        { key: 'month',    label: 'Month',    path: '/schedule/month-plan' },
        { key: 'week',     label: 'Week',     path: '/schedule/week' },
        { key: 'dispatch', label: 'Dispatch', path: '/schedule/day' },
        { key: 'jobs',     label: 'Jobs',     path: '/schedule/jobs' },
      ],

      '/communication': [
        { key: 'inbox',      label: 'Inbox',        path: '/communication/inbox' },
        { key: 'sms',        label: 'SMS',          path: '/communication/sms' },
        { key: 'calls',      label: 'Calls',        path: '/communication/calls' },
        { key: 'dialer',     label: 'Dialer',       path: '/communication/dialer' },
        { key: 'email',      label: 'Email',        path: '/communication/email' },
        { key: 'campaigns',  label: 'Campaigns',    path: '/communication/campaigns' },
      ],
    
      '/revival': [
        { key: 'overview', label: 'Overview', path: '/revival/overview' },
        { key: 'scanner', label: 'Scan Quote', path: '/revival/scanner' },
        { key: 'campaigns', label: 'Campaigns', path: '/revival/campaigns' },
        { key: 'planner', label: 'Planner', path: '/revival/planner' },
        { key: 'history', label: 'History', path: '/revival/history' },
        { key: 'opportunities', label: 'Revenue Opportunities', path: '/revival/ai' }
      ],
  
      '/analytics': [
        { key: 'overview',   label: 'Overview',     path: '/analytics' },
        { key: 'conversion', label: 'Conversion',   path: '/analytics/conversion' },
        { key: 'funnel',     label: 'Funnel',       path: '/analytics/funnel' },
        { key: 'cohorts',    label: 'Cohorts',      path: '/analytics/cohorts' },
        { key: 'revenue',    label: 'Revenue',      path: '/analytics/revenue' },
        { key: 'marketing',  label: 'Marketing',    path: '/analytics/marketing' },
      ],

      '/operations': [
        { key: 'office',      label: 'Office',           path: '/operations/office' },
        { key: 'sops',        label: 'SOPs',             path: '/operations/sops' },
        { key: 'documents',   label: 'Documents',        path: '/operations/documents' },
        { key: 'knowledge',   label: 'Knowledge',        path: '/operations/knowledge' },
        { key: 'technicians', label: 'Technicians',    path: '/operations/technicians' },
        { key: 'inventory',   label: 'Inventory',        path: '/operations/inventory' },
        { key: 'fleet',       label: 'Fleet',            path: '/operations/fleet' },
        /* Compliance hidden — page contains hardcoded stub data. Training lives on
           Team → Training (real rows: required reading + completions). */
      ],
  
      '/finance': [
        { key: 'invoices',      label: 'Invoices',       path: '/finance/invoices' },
        { key: 'payments',      label: 'Payments',       path: '/finance/payments' },
      ],
  
      '/marketing': [
        { key: 'campaigns',   label: 'Campaigns',    path: '/marketing/campaigns' },
      ],
  
      '/settings': [
        { key: 'workspace', label: 'Workspace', path: '/settings/workspace' },
        { key: 'communications', label: 'Communications', path: '/settings/communications' },
        { key: 'phone', label: 'Phone', path: '/settings/phone/call-flow' },
        { key: 'billing', label: 'Billing', path: '/settings/billing' },
        { key: 'compliance', label: 'Compliance', path: '/settings/compliance' },
        { key: 'portal', label: 'Portal', path: '/settings/portal' },
        { key: 'scheduling', label: 'Scheduling', path: '/settings/scheduling' },
        { key: 'knowledge', label: 'Assistant knowledge', path: '/settings/knowledge' },
        { key: 'team', label: 'Team', path: '/settings/team' },
        { key: 'appearance', label: 'Appearance', path: '/settings/appearance' },
      ],

      '/team': [
        { key: 'members',    label: 'Directory',  path: '/team' },
        { key: 'time-off',   label: 'Time off',   path: '/team/time-off' },
        { key: 'training',   label: 'Training',   path: '/team/training' },
        { key: 'compliance', label: 'Compliance', path: '/team/compliance' },
        { key: 'onboarding', label: 'Onboarding', path: '/team/onboarding' },
        { key: 'routing', label: 'Call Routing', path: '/settings/team' },
      ],
    },
  
    // Optional data schemas & filters (can be used to render tables/columns/forms)
    fieldSchemas: {
      leads:     ['name','email','phone_number','service','message','created_at'],
      customers: ['name','email','phone','address','notes'],
      invoices:  ['invoice_no','customer','amount','status','due_date','issued_at'],
    },
  
    filters: {
      leads: ['query','service','dateFrom','dateTo','sortBy'],
      customers: ['query','tag','lastSeenFrom','lastSeenTo','sortBy'],
      communication: ['query','channel','assignee','status','dateFrom','dateTo'],
      analytics: ['dateRange','segment','source'],
      finance: ['status','dateFrom','dateTo','min','max'],
      marketing: ['status','owner','channel'],
    },

    scheduling: {
      serviceLabel: 'Service Type',
      staffLabel: 'Assignee',
      locationLabel: 'Location',
      defaultDurationMins: 60,
    },
  
    // Feature flags (can be checked by routes/components to conditionally render)
    features: {
      subscriptions: false,
      operations: false,
      // Office/location dimension is landing in another lane. The dispatch board
      // ships the filter now, gated OFF, so it lights up the moment the backend
      // /scheduling/locations/ endpoint (and per-route office data) is real.
      locationFilter: false,
      // Access & Safety card on appointment detail. Built to Terminal B's frozen
      // contract (docs/ACCESS_SAFETY_FACTS.md). Reveal endpoint LIVE + contract-
      // verified (crm-api 2b47904c, 2026-08-24); founder-authorized flip. Renders
      // masked facts with tap-to-reveal (audited); empty-state when a customer has
      // none, so it's safe across tenants still awaiting extraction.
      accessSafetyCard: true,
    },
  };
  
  /* ───────────────── Industries (overrides/extensions) ───────────────── */
  
  const industries = {
    general: {
      ...base,
    },
  
    wholesaler: {
      labels: {
        customers: 'Cash Buyers',
        leads: 'Sellers',
        deals: 'Deals',
        buyers: 'Cash Buyers',
        sellers: 'Sellers',
      },
      nav: [
        { path: '/dashboard',     label: 'Dashboard',      icon: 'FiTrendingUp' },
        { path: '/leads',         label: 'Leads',          icon: 'FiFileText' },
        { path: '/customers',     label: 'Cash Buyers',    icon: 'FiUsers' },
        { path: '/communication', label: 'Communications', icon: 'FiMessageSquare' },
        { path: '/schedule',      label: 'Schedule',       icon: 'FiCalendar' },
        { path: '/analytics',     label: 'Analytics',      icon: 'FiTrendingUp' },
        { path: '/operations',    label: 'Operations',        icon: 'FiTruck' },
        { path: '/finance',       label: 'Finance',        icon: 'FiDollarSign' },
        /* Marketing hidden — hardcoded mock data */
        { path: '/settings',      label: 'Settings',       icon: 'FiSettings' },
      ],
      subnav: {
        ...base.subnav,
        '/customers': [
          { key: 'buyers',   label: 'Buyers List',  path: '/customers/list' },
          { key: 'import',   label: 'Upload CSV',   path: '/customers/import', roles: ['Admin','Manager'] },
          { key: 'followup', label: 'Follow-Ups',   path: '/customers/care' },
          { key: 'ai',       label: 'AI Insights',  path: '/customers/ai' },
        ],
        '/leads': [
          { key: 'new',            label: 'Website Leads',   path: '/leads/new' },
          { key: 'pipeline',       label: 'Pipeline View',   path: '/leads/pipeline' },
          { key: 'under_contract', label: 'Under Contract',  path: '/leads/under-contract' },
          { key: 'closed',         label: 'Closed Deals',    path: '/leads/closed' },
          { key: 'dead',           label: 'Disqualified',    path: '/leads/disqualified' },
        ],
      },
      fieldSchemas: {
        leads:     ['name','phone_number','email','property_address','ask_price','motivation','timeline','created_at'],
        customers: ['company','contact','phone','email','buy_box','notes'],
      },
      scheduling: {
        serviceLabel: 'Deal Type',
        staffLabel: 'Acquisition Rep',
        locationLabel: 'Property Address',
        defaultDurationMins: 30,
      },
      features: {
        ...base.features,
        subscriptions: false,
        operations: false,
      },
    },
  
    real_estate: {
      labels: {
        customers: 'Buyers',
        leads: 'Leads',
        deals: 'Transactions',
        buyers: 'Buyers',
        sellers: 'Sellers',
      },
      nav: [
        { path: '/dashboard',     label: 'Insights',        icon: 'FiTrendingUp' },
        { path: '/leads',         label: 'Seller Leads',    icon: 'FiFileText' },
        { path: '/customers',     label: 'Buyers',          icon: 'FiUsers' },
        { path: '/schedule',      label: 'Appointments',    icon: 'FiCalendar' },
        { path: '/communication', label: 'Communications',  icon: 'FiPhone' },
        { path: '/analytics',     label: 'Analytics',       icon: 'FiTrendingUp' },
        { path: '/finance',       label: 'Finance',         icon: 'FiDollarSign' },
        /* Marketing hidden — hardcoded mock data */
        { path: '/settings',      label: 'Settings',        icon: 'FiSettings' },
      ],
      subnav: {
        ...base.subnav,
        '/customers': [
          { key: 'buyers',   label: 'Cash Buyers',     path: '/customers/list' },
          { key: 'import',   label: 'Import List',     path: '/customers/import', roles: ['Admin','Manager'] },
          { key: 'offers',   label: 'Offers Made',     path: '/customers/care' },
          { key: 'insights', label: 'Market AI',       path: '/customers/ai' },
        ],
        '/leads': [
          { key: 'inbound',   label: 'Inbound Leads',  path: '/leads/new' },
          { key: 'pipeline',  label: 'Pipeline View',  path: '/leads/pipeline' }, // fixed path
          { key: 'closed',    label: 'Closed Deals',   path: '/leads/closed' },
          { key: 'dead',      label: 'Disqualified',   path: '/leads/disqualified' },
        ],
      },
      fieldSchemas: {
        leads:     ['name','phone_number','email','property_address','motivation','timeline','created_at'],
        customers: ['name','email','phone','criteria','budget','notes'],
      },
      scheduling: {
        serviceLabel: 'Showing Type',
        staffLabel: 'Agent',
        locationLabel: 'Property Address',
        defaultDurationMins: 45,
      },
      features: {
        ...base.features,
        subscriptions: false,
        operations: false,
      },
    },
  
    pest_control: {
      labels: {
        customers: 'Customers',
        leads: 'Inbound Requests',
        deals: 'Service Agreements',
      },
      // Add Operations to nav for pest (use an existing icon for now)
      nav: [
        { path: '/dashboard',     label: 'Dashboard',      icon: 'FiHome' },
        { path: '/leads',         label: 'Leads',          icon: 'FiFileText' },
        { path: '/customers',     label: 'Customers',      icon: 'FiUsers' },
        { path: '/schedule',      label: 'Schedule',       icon: 'FiCalendar' },
        { path: '/communication', label: 'Communications', icon: 'FiMessageSquare' },
        { path: '/analytics',     label: 'Analytics',      icon: 'FiTrendingUp' },
        { path: '/finance',       label: 'Finance',        icon: 'FiDollarSign' },
        { path: '/revival',       label: 'Quotes',         icon: 'FiClipboard' },
        /* Marketing hidden — hardcoded mock data */
        { path: '/sales',         label: 'Sales',          icon: 'FiShoppingBag', roles: ['admin','owner','ceo','manager','sales_rep','Admin','Manager','Owner'] },
        { path: '/operations',    label: 'Operations',        icon: 'FiTruck' }, // consider adding a truck/wrench icon in ICON_MAP
        { path: '/settings',      label: 'Settings',       icon: 'FiSettings' },
      ],
      subnav: {
        ...base.subnav,

        '/sales': [
          { key: 'map',        label: 'Field Map',           path: '/sales/map' },
          // Money pages first: AI Revival (pos 2) + Commissions (top-3) never
          // scroll into overflow. Utilities (Knock/Recordings/Sync) take the tail.
          { key: 'revival',    label: 'AI Revival',          path: '/sales/revival', badge: 'NEW', roles: ['admin','owner','ceo','manager','Admin','Manager','Owner'] },
          // Role-split surface: admin-level roles get the full management page
          // (mark paid, chargebacks); everyone else lands on My Commissions —
          // their own entries only, enforced by backend 403s.
          { key: 'commissions', label: 'Commissions',        path: '/sales/commissions' },
          { key: 'd2d',        label: 'D2D Sale',            path: '/sales/d2d' },
          { key: 'reports',    label: 'Knock Reports',       path: '/sales/reports', roles: ['admin','owner','ceo','manager','Admin','Manager','Owner'] },
          { key: 'recordings', label: 'Recordings',          path: '/sales/recordings', roles: ['admin','owner','ceo','manager','Admin','Manager','Owner'] },
          { key: 'sync',       label: 'FieldRoutes Sync',    path: '/sales/sync', roles: ['admin','owner','ceo','manager','Admin','Manager','Owner'] },
        ],

        // Web-lead triage + Kanban pipeline board
        '/leads': [
          { key: 'new',       label: 'New Requests',   path: '/leads/new' },
          { key: 'pipeline',  label: 'Pipeline',       path: '/leads/pipeline' },
        ],

        // No pest /schedule override — pest inherits the unified base subnav
        // (Month · Dispatch · Jobs). Dispatch stays the default surface for pest
        // via dispatchRegistry.getDefaultScheduleView('pest_control').

        // Deep operations vertical (pest)
        '/operations': [
          { key: 'sops',        label: 'SOPs',             path: '/operations/sops' },
          { key: 'knowledge',   label: 'Knowledge',        path: '/operations/knowledge' },
          { key: 'technicians', label: 'Technicians',      path: '/operations/technicians' },
          { key: 'inventory',   label: 'Inventory',        path: '/operations/inventory' },
          { key: 'fleet',       label: 'Fleet',            path: '/operations/fleet' },
          /* Compliance hidden — hardcoded stub data. Training = Team → Training. */
        ],

        '/settings': [
        { key: 'workspace', label: 'Workspace', path: '/settings/workspace' },
        { key: 'communications', label: 'Communications', path: '/settings/communications' },
        { key: 'phone', label: 'Phone', path: '/settings/phone/call-flow' },
        { key: 'billing', label: 'Billing', path: '/settings/billing' },
        { key: 'compliance', label: 'Compliance', path: '/settings/compliance' },
        { key: 'portal', label: 'Portal', path: '/settings/portal' },
        { key: 'scheduling', label: 'Scheduling', path: '/settings/scheduling' },
        { key: 'knowledge', label: 'Assistant knowledge', path: '/settings/knowledge' },
        { key: 'team', label: 'Team', path: '/settings/team' },
        { key: 'appearance', label: 'Appearance', path: '/settings/appearance' },
      ],


      },
      fieldSchemas: {
        leads:     ['name','phone_number','email','service','service_address','message','created_at'],
        customers: ['name','email','phone','service_plan','last_service','next_service'],
      },
      scheduling: {
        serviceLabel: 'Service Type',
        staffLabel: 'Technician',
        locationLabel: 'Service Address',
        defaultDurationMins: 90,
      },
      features: {
        ...base.features,
        subscriptions: false,
        operations: true,
      },
    },

    landscaping: {
      labels: {
        customers: 'Customers',
        leads: 'Inbound Requests',
        deals: 'Service Agreements',
      },
      nav: [
        { path: '/dashboard',     label: 'Dashboard',      icon: 'FiHome' },
        { path: '/leads',         label: 'Leads',          icon: 'FiFileText' },
        { path: '/customers',     label: 'Customers',      icon: 'FiUsers' },
        { path: '/schedule',      label: 'Schedule',       icon: 'FiCalendar' },
        { path: '/communication', label: 'Communications', icon: 'FiMessageSquare' },
        { path: '/analytics',     label: 'Analytics',      icon: 'FiTrendingUp' },
        { path: '/finance',       label: 'Finance',        icon: 'FiDollarSign' },
        /* Marketing hidden — hardcoded mock data */
        { path: '/sales',         label: 'Sales',          icon: 'FiShoppingBag', roles: ['admin','owner','ceo','manager','sales_rep','Admin','Manager','Owner'] },
        { path: '/operations',    label: 'Operations',        icon: 'FiTruck' },
        { path: '/settings',      label: 'Settings',       icon: 'FiSettings' },
      ],
      subnav: {
        ...base.subnav,
        '/sales': [
          // Money pages first (same rule as pest): the campaign review /
          // AI Revival surface is vertical-agnostic — every vertical with a
          // Sales section registers it, position 1.
          { key: 'revival', label: 'AI Campaigns', path: '/sales/revival', badge: 'NEW', roles: ['admin','owner','ceo','manager','Admin','Manager','Owner'] },
          // Commissions is vertical-agnostic (CommissionAdmin route is registered
          // for every industry in SalesRouter). Landscaping has a Sales section and
          // D2D reps who earn commission, so it must register the entry — omitting
          // it left the page reachable by URL but silently absent from nav.
          // Executive-only, matching pest's gating.
          { key: 'commissions', label: 'Commissions', path: '/sales/commissions' },
          { key: 'd2d', label: 'D2D Sale', path: '/sales/d2d' },
        ],
        '/schedule': [
          // Month first — landscaping thinks in weeks/months; it's the
          // vertical's default surface (dispatchRegistry.defaultScheduleView).
          { key: 'month',    label: 'Month',      path: '/schedule/month-plan' },
          { key: 'week',     label: 'Week',       path: '/schedule/week' },
          { key: 'dispatch', label: 'Dispatch',   path: '/schedule/day' },
          // Trades jobs: won quote → job → visits/crew/materials/margin.
          { key: 'jobs',     label: 'Jobs',       path: '/schedule/jobs' },
        ],
        '/operations': [
          { key: 'sops',        label: 'SOPs',             path: '/operations/sops' },
          { key: 'documents',   label: 'Documents',        path: '/operations/documents' },
          { key: 'knowledge',   label: 'Knowledge',        path: '/operations/knowledge' },
          { key: 'crews',      label: 'Crews',        path: '/operations/technicians' },
          { key: 'inventory',  label: 'Inventory',    path: '/operations/inventory' },
          { key: 'fleet',      label: 'Fleet',        path: '/operations/fleet' },
          /* Equipment hidden — hardcoded stub data. Training = Team → Training. */
        ],
      },
      fieldSchemas: {
        leads: ['name','phone_number','email','service','service_address','message','created_at'],
        customers: ['name','email','phone','service_plan','last_service','next_service'],
      },
      scheduling: {
        serviceLabel: 'Service Program',
        staffLabel: 'Crew Lead',
        locationLabel: 'Property Address',
        defaultDurationMins: 120,
        // Mirrors backend scheduling_app/scheduling_packs.py "landscaping".
        // Source of truth is GET /api/scheduling/config/; these are the
        // render-time defaults so the board is correct before that resolves.
        resourceModel: 'crew',        // dispatch/route/aggregate by crew, not person
        jobModel: 'recurring_visit',  // maintenance visits + linked install days
        supportsMultiDay: true,       // installs span linked single-day visits
        multiDayStrategy: 'discrete_linked_visits',
        excludedWeekdays: [6],        // 0=Mon…6=Sun; crews off Sundays
        customerConfirmsVisits: false, // subscription: no per-mow confirmation
      },
      features: {
        ...base.features,
        subscriptions: false,
        operations: true,
      },
    },

    fitness: {
      labels: {
        customers: 'Members',
        leads: 'Prospects',
        deals: 'Packages',
      },
      nav: [
        { path: '/dashboard',     label: 'Dashboard',      icon: 'FiHome' },
        { path: '/leads',         label: 'Prospects',      icon: 'FiFileText' },
        { path: '/customers',     label: 'Members',        icon: 'FiUsers' },
        { path: '/schedule',      label: 'Schedule',       icon: 'FiCalendar' },
        { path: '/communication', label: 'Communications', icon: 'FiMessageSquare' },
        { path: '/analytics',     label: 'Analytics',      icon: 'FiTrendingUp' },
        { path: '/finance',       label: 'Finance',        icon: 'FiDollarSign' },
        /* Marketing hidden — hardcoded mock data */
        { path: '/settings',      label: 'Settings',       icon: 'FiSettings' },
      ],
      subnav: {
        ...base.subnav,
        '/leads': [
          { key: 'new',       label: 'New Prospects', path: '/leads/new' },
          { key: 'trial',     label: 'Trials',        path: '/leads/trial' },
          { key: 'booked',    label: 'Booked',        path: '/leads/booked' },
          { key: 'won',       label: 'Won',           path: '/leads/closed' },
          { key: 'lost',      label: 'Lost',          path: '/leads/disqualified' },
        ],
        '/customers': [
          { key: 'list',   label: 'Members',       path: '/customers/list' },
          { key: 'import', label: 'Import',        path: '/customers/import', roles: ['Admin','Manager'] },
          { key: 'care',   label: 'Member Care',   path: '/customers/care' },
          { key: 'ai',     label: 'AI Coach',      path: '/customers/ai' },
        ],
        '/finance': [
          { key: 'invoices',      label: 'Invoices',      path: '/finance/invoices' },
          { key: 'payments',      label: 'Payments',      path: '/finance/payments' },
        ],
      },
      fieldSchemas: {
        leads:     ['name','phone_number','email','preferred_time','package_type','instructor_pref','created_at'],
        customers: ['name','email','phone','membership','trainer','renewal_date'],
      },
      scheduling: {
        serviceLabel: 'Session Type',
        staffLabel: 'Trainer',
        locationLabel: 'Studio',
        defaultDurationMins: 60,
      },
      features: {
        ...base.features,
        subscriptions: true,
        operations: false,
      },
    },

    food_wellness: {
      labels: {
        customers: 'Subscribers',
        leads: 'Requests',
        deals: 'Subscriptions',
      },
      nav: [
        { path: '/dashboard',     label: 'Dashboard',      icon: 'FiHome' },
        { path: '/leads',         label: 'Requests',       icon: 'FiFileText' },
        { path: '/customers',     label: 'Subscribers',    icon: 'FiUsers' },
        { path: '/schedule',      label: 'Schedule',       icon: 'FiCalendar' },
        { path: '/communication', label: 'Communications', icon: 'FiMessageSquare' },
        { path: '/analytics',     label: 'Analytics',      icon: 'FiTrendingUp' },
        { path: '/finance',       label: 'Finance',        icon: 'FiDollarSign' },
        { path: '/revival',       label: 'Quotes',         icon: 'FiClipboard' },
        { path: '/operations',    label: 'Operations',        icon: 'FiTruck' },
        /* Marketing hidden — hardcoded mock data */
        { path: '/settings',      label: 'Settings',       icon: 'FiSettings' },
      ],
      subnav: {
        ...base.subnav,
        '/leads': [
          { key: 'new',            label: 'New Requests',   path: '/leads/new' },
          { key: 'contacted',      label: 'Contacted',      path: '/leads/contacted' },
          { key: 'pipeline',       label: 'Pipeline View',  path: '/leads/pipeline' },
          { key: 'under_contract', label: 'Under Contract', path: '/leads/under-contract' },
          { key: 'closed',         label: 'Closed',         path: '/leads/closed' },
          { key: 'disqualified',   label: 'Disqualified',   path: '/leads/disqualified' },
        ],
        '/customers': [
          { key: 'list',   label: 'Subscribers',      path: '/customers/list' },
          { key: 'import', label: 'Import',           path: '/customers/import', roles: ['Admin','Manager'] },
          { key: 'care',   label: 'Subscriber Care',  path: '/customers/care' },
          { key: 'ai',     label: 'AI Insights',      path: '/customers/ai' },
          { key: 'revival', label: 'Quotes',   path: '/customers/revival' },
        ],
      },
      fieldSchemas: {
        ...base.fieldSchemas,
      },
      scheduling: {
        serviceLabel: 'Plan Type',
        staffLabel: 'Coordinator',
        locationLabel: 'Delivery Area',
        defaultDurationMins: 60,
      },
      features: {
        ...base.features,
        subscriptions: true,
        operations: false,
      },
    },
  
    events: {
      labels: {
        customers: 'Clients',
        leads: 'Inquiries',
        deals: 'Bookings',
      },
      nav: [
        { path: '/dashboard',     label: 'Dashboard',      icon: 'FiHome' },
        { path: '/leads',         label: 'Inquiries',      icon: 'FiFileText' },
        { path: '/customers',     label: 'Clients',        icon: 'FiUsers' },
        { path: '/schedule',      label: 'Schedule',       icon: 'FiCalendar' },
        // Quotes is the booking engine for events (deposit/balance quotes feed
        // the calendar) — omitting it orphaned the primary daily action.
        { path: '/revival',       label: 'Quotes',         icon: 'FiClipboard' },
        // Operations for a venue is its paperwork — signed contracts, waivers,
        // and the SOPs staff follow on the day. Not routes, crews or fleet.
        { path: '/operations',    label: 'Operations',     icon: 'FiTruck' },
        { path: '/communication', label: 'Communications', icon: 'FiMessageSquare' },
        { path: '/analytics',     label: 'Analytics',      icon: 'FiTrendingUp' },
        { path: '/finance',       label: 'Finance',        icon: 'FiDollarSign' },
        { path: '/settings',      label: 'Settings',       icon: 'FiSettings' },
      ],
      subnav: {
        /* A venue's Operations is NOT the base list. Office, Knowledge,
           Technicians, Inventory and Fleet are meaningless for RT's Ranch, and
           inheriting them would put five dead tabs behind one live one. Two
           tabs that both do something beats seven where five don't. */
        ...base.subnav,
        // AFTER the spread: in an object literal the later key wins, so this must follow
        // ...base.subnav or the base seven-tab list silently replaces it.
        '/operations': [
          { key: 'documents',   label: 'Documents',        path: '/operations/documents' },
          { key: 'sops',        label: 'SOPs',             path: '/operations/sops' },
        ],
        '/leads': [
          { key: 'new',       label: 'New Inquiries', path: '/leads/new' },
          { key: 'pipeline',  label: 'Pipeline',      path: '/leads/pipeline' },
        ],
        '/customers': [
          { key: 'list',   label: 'Clients',       path: '/customers/list' },
          { key: 'import', label: 'Import',         path: '/customers/import', roles: ['Admin','Manager'] },
          { key: 'care',   label: 'Client Care',    path: '/customers/care' },
          { key: 'ai',     label: 'AI Insights',    path: '/customers/ai' },
          { key: 'revival', label: 'Quotes',        path: '/customers/revival' },
        ],
        '/schedule': [
          { key: 'dispatch', label: 'Calendar',     path: '/schedule/day' },
        ],
      },
      fieldSchemas: {
        leads:     ['name','phone_number','email','event_type','event_date','guest_count','venue_preference','budget_range','created_at'],
        customers: ['name','email','phone','event_type_preference','organization_name','role','notes'],
      },
      scheduling: {
        serviceLabel: 'Event Type',
        staffLabel: 'Coordinator',
        locationLabel: 'Venue',
        defaultDurationMins: 240,
      },
      features: {
        ...base.features,
        subscriptions: false,
        // Turned on with the Documents route, not before — the nav item and the
        // page it reaches ship in the same deploy.
        operations: true,
      },
    },
  };
  
  /* ───────────────── Overrides & helpers ───────────────── */
  
  function getActiveTenant() {
    try {
      const raw = localStorage.getItem('activeTenant');
      return raw && raw !== 'undefined' ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
  
  export function getIndustryKey(fallback = 'general') {
    const tenant = getActiveTenant();
    const k = (tenant?.industry || '').toLowerCase();
    return industries[k] ? k : fallback;
  }
  
  function readOverrides() {
    try {
      const raw = localStorage.getItem('ui_overrides');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }
  
  function deepMerge(a, b) {
    if (!b) return a;
    const out = Array.isArray(a) ? [...a] : { ...a };
    for (const k of Object.keys(b)) {
      if (a && typeof a[k] === 'object' && !Array.isArray(a[k])) {
        out[k] = deepMerge(a[k] || {}, b[k]);
      } else {
        out[k] = b[k];
      }
    }
    return out;
  }
  
  export function getIndustryConfig(industryKey) {
    const baseCfg = industries[industryKey] || industries.general;
    const overrides = readOverrides();
    const all = overrides?.all || {};
    const per = overrides?.industries?.[industryKey] || {};
    return deepMerge(deepMerge(baseCfg, all), per);
  }
  
  export function roleAllows(itemRoles, userRole = 'Member') {
    if (!itemRoles || !itemRoles.length) return true;
    // Case-insensitive: prod role vocabulary is Title Case ("Admin", "Manager")
    // while registry lists historically mixed cases — match on meaning, not case.
    const normalized = String(userRole).trim().toLowerCase();
    return itemRoles.some(r => String(r).trim().toLowerCase() === normalized);
  }
  
  export function filterByRole(items = [], userRole = 'Member') {
    return items.filter(i => roleAllows(i.roles, userRole));
  }
  
  /* ───────── Public API used across app ───────── */
  
  export function getLabels(industryKey, key) {
    const cfg = getIndustryConfig(industryKey);
    return key ? (cfg.labels?.[key] ?? industries.general.labels[key]) : (cfg.labels || industries.general.labels);
  }
  
export function getNavForIndustry(industryKey, userRole = 'Member') {
  const cfg = getIndustryConfig(industryKey);
  const nav = filterByRole(cfg.nav || industries.general.nav, userRole);
  if (!nav.some((item) => item.path === '/team')) {
    nav.splice(Math.max(nav.length - 1, 0), 0, { path: '/team', label: 'Team', icon: 'FiUserPlus' });
  }
  return nav;
}
  
  // Cross-cutting subnav tabs that EVERY vertical gets, regardless of its industry
  // subnav override. Add a universal tab HERE — not to `general` and not to one
  // industry — so it can't silently miss a vertical the way the lead Work Queue did
  // (it lived only in general.subnav['/leads'], so pest_control/events, which define
  // their own '/leads', never showed it while landscaping — no override → inherited
  // general — did). We deliberately do NOT merge all of general in: that would re-add
  // vertical-inappropriate tabs (e.g. real-estate 'Under Contract'/'Closed') to pest.
  const UNIVERSAL_SUBNAV = {
    '/leads': [
      { key: 'queue', label: 'Work Queue', path: '/leads/queue' },
    ],
    // Partner prospects: a partner tenant's audience worked as prospects (email-only,
    // never customers). Universal on purpose — it first shipped only in pest_control's
    // '/sales' and was invisible on Hodges (landscaping), the Work Queue miss again.
    '/sales': [
      { key: 'partners', label: 'Partner prospects', path: '/sales/partners', roles: ['admin','owner','ceo','manager','Admin','Manager','Owner'] },
    ],
    // Audiences: hashed ad-network audience export behind a recorded owner authorization.
    // Universal for the same reason as Partner prospects — every industry with ad spend
    // gets it, no per-vertical '/marketing' override can hide it.
    '/marketing': [
      { key: 'audiences', label: 'Audiences', path: '/marketing/audiences', roles: ['admin','owner','ceo','manager','Admin','Manager','Owner'] },
    ],
  };

  export function getSubNavForPage(basePath, industryKey, userRole = 'Member') {
    const cfg = getIndustryConfig(industryKey);
    const industrySub = cfg.subnav?.[basePath] || industries.general.subnav?.[basePath] || [];
    const universal = UNIVERSAL_SUBNAV[basePath] || [];
    // Universal entries first (in their order), then industry entries; deduped by
    // key with the industry entry winning if it redefines a universal key.
    const seen = new Set();
    const merged = [];
    for (const item of universal) {
      const override = industrySub.find((i) => i.key === item.key);
      merged.push(override || item);
      seen.add(item.key);
    }
    for (const item of industrySub) {
      if (!seen.has(item.key)) { merged.push(item); seen.add(item.key); }
    }
    return filterByRole(merged, userRole);
  }
  
  export function getFieldSchema(section, industryKey) {
    const cfg = getIndustryConfig(industryKey);
    return cfg.fieldSchemas?.[section] || industries.general.fieldSchemas?.[section] || [];
  }
  
  export function getFiltersFor(section, industryKey) {
    const cfg = getIndustryConfig(industryKey);
    return cfg.filters?.[section] || industries.general.filters?.[section] || [];
  }

  export function getSchedulingConfig(industryKey) {
    const cfg = getIndustryConfig(industryKey);
    return cfg.scheduling || industries.general.scheduling || {};
  }

export function getFeatures(industryKey) {
  const cfg = getIndustryConfig(industryKey);
  return cfg.features || industries.general.features || {};
}

/**
 * Industry vocabulary for a "person who does the work" — used by Settings, empty
 * states, placeholders and help text so an events tenant never reads "technician"
 * and a landscaping tenant reads "crew", not "technician". Singular + plural + a
 * short noun for the work a team member does.
 * Pest keeps "technician" (correct for pest); general is industry-neutral.
 */
const TEAM_VOCAB = {
  events:        { member: 'coordinator',  members: 'coordinators',  work: 'event' },
  landscaping:   { member: 'crew member',  members: 'crew',          work: 'visit' },
  pest_control:  { member: 'technician',   members: 'technicians',   work: 'service' },
  real_estate:   { member: 'agent',        members: 'agents',        work: 'showing' },
  fitness:       { member: 'trainer',      members: 'trainers',      work: 'session' },
  general:       { member: 'team member',  members: 'team members',  work: 'job' },
};

export function getTeamVocab(industryKey) {
  const key = (industryKey || getIndustryKey('general') || 'general').toLowerCase();
  return TEAM_VOCAB[key] || TEAM_VOCAB.general;
}

/** Capitalize the first letter — for a vocab word used at the start of a label. */
export function cap(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/* ───────── Lightweight UI registry for industry labels/panels ───────── */
export const uiRegistry = {
  landscaping: {
    primaryEntity: 'Job',
    pipelineStages: ['Scheduled', 'In Progress', 'Completed', 'Invoiced'],
    customerPanels: ['Active Jobs', 'Service Schedule', 'Outstanding Balance'],
  },
  general: {
    primaryEntity: 'Job',
    pipelineStages: ['New', 'In Progress', 'Completed', 'Invoiced'],
    customerPanels: ['Active Jobs', 'Upcoming Work', 'Billing'],
  },
};

export function getUiRegistry(industryKey = 'general') {
  const key = industryKey?.toLowerCase();
  // Fall back to the industry-neutral `general` config, NOT landscaping. The old
  // landscaping fallback silently gave every unknown/unlisted industry — events and
  // pest_control among them — landscaping vocabulary (crew/property/visit), which is
  // exactly the wrong-vocabulary bug this sweep fixes. `general` is deliberately
  // neutral (Service Type / Assignee / Location).
  return uiRegistry[key] || uiRegistry.general;
}
